import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, LogOut, Coffee, AlertCircle, ListChecks, BarChart3, Cloud, CloudOff, Satellite, Users, Bell, BatteryCharging, KeyRound } from 'lucide-react';
import VisitsTab from '../components/staff/VisitsTab';
import BreaksTab from '../components/staff/BreaksTab';
import MissingLogTab from '../components/staff/MissingLogTab';
import TodosTab from '../components/staff/TodosTab';
import ReportTab from '../components/staff/ReportTab';
import TeamTab from '../components/staff/TeamTab';
import AlertWatcher from '../components/AlertWatcher';
import AlertCenter from '../components/AlertCenter';
import VersionBadge from '../components/VersionBadge';
import CarryOverAlert from '../components/staff/CarryOverAlert';
import BatterySettingsWizard, { BATTERY_WIZARD_STORAGE_KEY } from '../components/staff/BatterySettingsWizard';
import BatteryOptIndicator from '../components/staff/BatteryOptIndicator';
import GpsDiagnostic from '../components/staff/GpsDiagnostic';
import RecentOtpsModal from '../components/staff/RecentOtpsModal';
import { startGpsTracking, stopGpsTracking, getQueueSize, getTrackerState, setActiveVisitId } from '../lib/gpsTracker';
import { startAppSessionTracker, stopAppSessionTracker } from '../lib/appSessionTracker';
import { startPoll } from '../lib/poll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_TABS = [
  { id: 'visits', label: 'Visits', icon: MapPin },
  { id: 'breaks', label: 'Breaks', icon: Coffee },
  { id: 'missing', label: 'Missing', icon: AlertCircle },
  { id: 'todos', label: 'To Do', icon: ListChecks },
  { id: 'team', label: 'Team', icon: Users, requiresTeam: true },
  { id: 'report', label: 'Report', icon: BarChart3 },
];

export default function StaffDashboard() {
  const [activeTab, setActiveTab] = useState('visits');
  const [user, setUser] = useState({});
  const [online, setOnline] = useState(navigator.onLine);
  const [queueSize, setQueueSize] = useState(0);
  const [tracker, setTracker] = useState({ mode: 'idle', active: false });
  const [counts, setCounts] = useState({ todos_pending: 0, missing_log_count: 0, todos_admin_assigned: 0 });
  const [hasTeam, setHasTeam] = useState(false);
  const [showAlertCenter, setShowAlertCenter] = useState(false);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [showBatteryWizard, setShowBatteryWizard] = useState(false);
  const [showGpsDiagnostic, setShowGpsDiagnostic] = useState(false);
  const [showRecentOtps, setShowRecentOtps] = useState(false);
  const navigate = useNavigate();

  // Auto-show the battery-optimisation wizard on Android whenever the
  // OS-level "Ignore battery optimisations" is NOT set for Carloo Tex.
  //
  // Old behaviour (pre Feb 22, 2026): only popped once per install, then
  // was silent forever even if the staff never actually completed the
  // steps (or the flag was set by a bug on some Android WebView builds).
  // AZAD's 4:30 pm miss on prod was traced to Battery Saver killing the
  // background service — his device still had optimisation ON.
  //
  // New behaviour: check the BatteryOptPlugin on every dashboard mount.
  // If native + `ignored === false` we clear the "done" flag and re-open
  // the wizard so the staff can fix it. If the plugin isn't available
  // (older APK), fall back to the legacy "show once per install" gate.
  useEffect(() => {
    const isAndroid = /android/i.test(navigator.userAgent || '');
    if (!isAndroid) return;

    let cancelled = false;
    (async () => {
      let native = false, ignored = null;
      try {
        const { getBatteryOptStatus } = await import('../lib/batteryOptStatus');
        const r = await getBatteryOptStatus();
        native = !!r.native;
        ignored = r.ignored;
      } catch (_) { /* plugin unavailable — fall through to legacy gate */ }

      if (cancelled) return;
      const done = (() => { try { return localStorage.getItem(BATTERY_WIZARD_STORAGE_KEY); } catch (_) { return null; } })();

      // Priority 1: native plugin says battery optimisation is STILL ON.
      //   → force the wizard even if the user previously dismissed it.
      if (native && ignored === false) {
        try { localStorage.removeItem(BATTERY_WIZARD_STORAGE_KEY); } catch (_) { /* ignore */ }
        const t = setTimeout(() => setShowBatteryWizard(true), 1500);
        return () => clearTimeout(t);
      }
      // Priority 2: first-time / no plugin → legacy one-shot pop.
      if (!done) {
        const t = setTimeout(() => setShowBatteryWizard(true), 1500);
        return () => clearTimeout(t);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);

    (async () => {
      // PRIVACY: only auto-start the tracker if the staff currently has an
      // open attendance session for today. If they're already checked out
      // (logged in after-hours to read alerts), GPS stays off. It will start
      // automatically on the next Check-in tap (see CheckInOut.js).
      let shouldTrack = true;
      try {
        const token = localStorage.getItem('token');
        const ar = await fetch(`${API_URL}/api/attendance/today`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (ar.ok) {
          const att = await ar.json();
          const open = att?.has_open_session ?? !!(att?.check_in && !att?.check_out);
          if (att?.check_out && !open) shouldTrack = false;
        }
      } catch (_) { /* fail open — better to track than miss */ }
      if (shouldTrack) {
        await startGpsTracking({ intervalSeconds: 30 });
      }
      startAppSessionTracker();
      // Note: Screen wake-lock intentionally removed (Mar 2026) so the phone
      // can sleep / auto-lock normally while checked in. Background GPS still
      // runs via the @capacitor-community/background-geolocation foreground
      // service on Android and CLLocationManager allowsBackgroundLocationUpdates
      // on iOS — neither needs the screen to stay on.
      setTracker(getTrackerState());
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/visits`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (r.ok) {
          const visits = await r.json();
          const ongoing = visits.find(v => v.status === 'ongoing');
          setActiveVisitId(ongoing ? ongoing.id : null);
        }
      } catch {}
      fetchCounts();
      fetchHasTeam();
    })();

    const onlineHandler = () => setOnline(true);
    const offlineHandler = () => setOnline(false);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    // Local tracker state stays at 15 s (no network); server polls moved to
    // a 60 s visibility-aware poller (v2.6.4 — was every 15 s).
    const statusInterval = setInterval(async () => {
      setQueueSize(await getQueueSize());
      setTracker(getTrackerState());
    }, 15000);
    const stopServerPoll = startPoll(() => { fetchCounts(); fetchUnreadAlertCount(); }, 60_000);

    fetchUnreadAlertCount();

    return () => {
      stopGpsTracking();
      stopAppSessionTracker();
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
      clearInterval(statusInterval);
      stopServerPoll();
    };
  }, []);

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/staff/today-summary`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setCounts({
          todos_pending: d.todos_pending,
          todos_admin_assigned: d.todos_admin_assigned,
          missing_log_count: d.missing_log_count
        });
      }
    } catch {}
  };

  const fetchHasTeam = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/team/my-team`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setHasTeam(Array.isArray(d) && d.length > 0);
      }
    } catch {}
  };

  const fetchUnreadAlertCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/alerts/unread`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setUnreadAlertCount(Array.isArray(d) ? d.length : 0);
      }
    } catch {}
  };

  const handleLogout = () => {
    stopGpsTracking();
    stopAppSessionTracker();
    setActiveVisitId(null);
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 safe-area-pb">
      {/* Top bar */}
      <div className="bg-white shadow-sm sticky top-0 z-40 safe-top">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-blue-700">{(user.name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-sm truncate">{user.name}</h2>
              <p className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                <span className="truncate">{user.mobile}</span>
                <VersionBadge />
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {tracker.mode === 'native' && tracker.active && (
              <span
                className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide"
                data-testid="bg-gps-pill"
              >
                <Satellite size={11} />
                <span>BG GPS</span>
              </span>
            )}
            <div
              className="flex items-center space-x-1 text-xs cursor-pointer hover:opacity-80"
              data-testid="gps-status"
              onClick={() => setShowGpsDiagnostic(true)}
              title="Tap for GPS diagnostic"
            >
              {online ? <Cloud className="text-green-500" size={14} /> : <CloudOff className="text-orange-500" size={14} />}
              {queueSize > 0 && <span className="text-orange-600 font-medium">{queueSize}</span>}
            </div>
            <button
              onClick={() => setShowBatteryWizard(true)}
              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
              data-testid="battery-wizard-open"
              title="Optimise battery settings"
            >
              <BatteryCharging size={18} />
            </button>
            <BatteryOptIndicator onOpenWizard={() => setShowBatteryWizard(true)} />
            <button
              onClick={() => setShowRecentOtps(true)}
              className="p-2 text-indigo-700 hover:bg-indigo-50 rounded-lg"
              data-testid="recent-otps-open"
              title="Show my recent OTPs"
            >
              <KeyRound size={18} />
            </button>
            <button
              onClick={() => setShowAlertCenter(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              data-testid="alert-center-open"
              title="Alert Center"
            >
              <Bell size={18} />
              {unreadAlertCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white"
                  data-testid="alert-center-badge"
                >{unreadAlertCount}</span>
              )}
            </button>
            <button onClick={handleLogout} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid="staff-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <CarryOverAlert />
        {activeTab === 'visits' && <VisitsTab />}
        {activeTab === 'breaks' && <BreaksTab />}
        {activeTab === 'missing' && <MissingLogTab />}
        {activeTab === 'todos' && <TodosTab />}
        {activeTab === 'team' && <TeamTab />}
        {activeTab === 'report' && <ReportTab />}
      </div>

      {/* Real-time alerts (managers only — endpoint returns [] for non-managers) */}
      <AlertWatcher />

      {/* Alert Center overlay */}
      {showAlertCenter && (
        <div className="fixed inset-0 z-[55] bg-gray-50 overflow-y-auto" data-testid="alert-center-overlay">
          <div className="bg-white sticky top-0 z-10 px-4 py-3 border-b flex items-center justify-between">
            <h2 className="font-semibold">Alerts</h2>
            <button
              onClick={() => { setShowAlertCenter(false); fetchUnreadAlertCount(); }}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm"
              data-testid="alert-center-close"
            >Close</button>
          </div>
          <div className="p-4">
            <AlertCenter compact />
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 safe-bottom">
        <div className="flex justify-around items-center py-2 px-1 max-w-screen-md mx-auto">
          {ALL_TABS.filter(t => !t.requiresTeam || hasTeam).map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            const badge =
              t.id === 'todos' ? counts.todos_pending :
              t.id === 'missing' ? counts.missing_log_count : 0;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`relative flex flex-col items-center space-y-0.5 px-3 py-1.5 rounded-lg transition-all ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-500'}`}
                data-testid={`nav-${t.id}`}
              >
                <Icon size={22} />
                <span className="text-[10px] font-medium">{t.label}</span>
                {badge > 0 && (
                  <span
                    className={`absolute top-0 right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${t.id === 'missing' ? 'bg-red-500' : 'bg-emerald-500'} text-white`}
                    data-testid={`nav-${t.id}-badge`}
                  >{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Battery-optimisation wizard — auto-shows once on first launch, also re-openable from top bar */}
      <BatterySettingsWizard
        open={showBatteryWizard}
        onClose={() => setShowBatteryWizard(false)}
      />

      {/* GPS Diagnostic — self-serve health check tap-in on the top-bar GPS pill */}
      <GpsDiagnostic
        open={showGpsDiagnostic}
        onClose={() => setShowGpsDiagnostic(false)}
      />

      {/* Recent-OTPs recovery modal — Jul 2, 2026 hotfix so staff can
          retrieve any gate OTP generated today (defence against dropped
          modals from network hiccups / double-taps / phone backgrounding). */}
      <RecentOtpsModal
        open={showRecentOtps}
        onClose={() => setShowRecentOtps(false)}
      />
    </div>
  );
}
